import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { Handshake, Plus, Pencil, Trash2, ExternalLink, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPartners() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    description: '',
    logo_url: '',
    website_url: '',
    email: ''
  });

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      toast.error('Accesso negato - Solo admin');
      navigate('/dashboard');
      return;
    }
    fetchPartners();
  }, [user]);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API_URL}/partners`);
      setPartners(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento partner');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.type || !formData.description || !formData.logo_url || !formData.website_url) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    try {
      if (editingPartner) {
        await axios.put(`${API_URL}/partners/${editingPartner.id}`, formData);
        toast.success('Partner aggiornato con successo');
      } else {
        await axios.post(`${API_URL}/partners`, formData);
        toast.success('Partner creato con successo');
      }
      
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const deletePartner = async (partnerId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo partner?')) return;
    
    try {
      await axios.delete(`${API_URL}/partners/${partnerId}`);
      toast.success('Partner eliminato');
      fetchPartners();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  const startEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      type: partner.type,
      name: partner.name,
      description: partner.description,
      logo_url: partner.logo_url,
      website_url: partner.website_url,
      email: partner.email || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      type: '',
      name: '',
      description: '',
      logo_url: '',
      website_url: '',
      email: ''
    });
    setEditingPartner(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Caricamento...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Handshake className="w-8 h-8 text-blue-600" />
              Gestione Partner Tecnici
            </h1>
            <p className="text-gray-600 mt-1">Gestisci i partner che appariranno nella homepage</p>
          </div>
          <Button 
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {showForm ? 'Annulla' : 'Nuovo Partner'}
          </Button>
        </div>

        {/* Form */}
        {showForm && (
          <Card>
            <CardHeader>
              <CardTitle>
                {editingPartner ? 'Modifica Partner' : 'Nuovo Partner'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nome Partner *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="es. Hotel Paradise"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="type">Tipo *</Label>
                    <Input
                      id="type"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      placeholder="es. accommodation, catering, transport"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="logo_url">URL Logo *</Label>
                    <Input
                      id="logo_url"
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://..."
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="website_url">URL Sito Web *</Label>
                    <Input
                      id="website_url"
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      placeholder="https://..."
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email (opzionale)</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="info@partner.com"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Descrizione *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="es. Hai bisogno di alloggio? Contatta il nostro partner..."
                    rows={3}
                    required
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit">
                    {editingPartner ? 'Aggiorna' : 'Crea'} Partner
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Annulla
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Partners List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="text-center py-12">
                <Handshake className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nessun partner ancora. Creane uno!</p>
              </CardContent>
            </Card>
          ) : (
            partners.map((partner) => (
              <Card key={partner.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col items-center text-center space-y-3">
                    {/* Logo */}
                    <img 
                      src={partner.logo_url} 
                      alt={partner.name}
                      className="w-32 h-20 object-contain rounded"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/200x100?text=Logo';
                      }}
                    />
                    
                    {/* Name & Type */}
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">{partner.name}</h3>
                      <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                        {partner.type}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {partner.description}
                    </p>

                    {/* Links */}
                    <div className="flex gap-2 w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => window.open(partner.website_url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Sito
                      </Button>
                      {partner.email && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => window.location.href = `mailto:${partner.email}`}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Email
                        </Button>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 w-full pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => startEdit(partner)}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Modifica
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex-1"
                        onClick={() => deletePartner(partner.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Elimina
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
